import { useEffect, useMemo, useState, useRef } from 'react';

import { useUniversalPreset } from '@atlaskit/editor-core/preset-universal';
import { usePreset } from '@atlaskit/editor-core/use-preset';
import { blockControlsPlugin } from '@atlaskit/editor-plugins/block-controls';
import { editorViewModePlugin } from '@atlaskit/editor-plugins/editor-viewmode';
import { contextMenuPlugin } from '@atlaskit/editor-plugins/context-menu';
import { editorViewModeEffectsPlugin } from '@atlaskit/editor-plugin-editor-viewmode-effects';
import { loomPlugin } from '@atlaskit/editor-plugins/loom';
import { highlightPlugin } from '@atlaskit/editor-plugins/highlight';
import { selectionMarkerPlugin } from '@atlaskit/editor-plugins/selection-marker';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import type { ExtractPresetAPI } from '@atlaskit/editor-common/preset';

import { PageNextEditorPlugin } from '@atlassian/editor-plugin-ai/ConfluencePrebuilt';
import { referentialityPlugin } from '@atlassian/editor-referentiality';

import { type LoomProviderConfig } from '@confluence/fabric-providers';
import { fg } from '@confluence/feature-gating';
import { useIsLivePage } from '@confluence/live-pages-utils/entry-points/useIsLivePage';
import { useLoomRecorderEntryPoint } from '@confluence/loom-utils';
import {
	type MentionType,
	useMentionsNotifications,
	handleMentionsChanged,
} from '@confluence/mentions-notifications';
import { useBooleanFeatureFlag, useSessionData } from '@confluence/session-data';
import {
	EditorHeadingLinksPlugin,
	getIsHeadingLinksEnabled,
} from '@confluence/editor-heading-links';
import type { WithFlagsProps } from '@confluence/flags';
import { useLoomPluginOptions } from '@confluence/loom-editor-utils';
import { fabricExtensionManagerPlugin } from '@confluence/fabric-extension-manager-plugin';

import { useInitializeLoomPlugin } from '../hooks/useInitializeLoomPlugin';
import { ssrPlugin } from '../plugins/ssrPlugin';
import { pageTitleUtilsPlugin } from '../plugins/pageTitleUtilsPlugin';
import { EditorLoomButton } from '../EditorLoomButton';

import type { FullPageEditorProps } from './ComposableEditor';

export type FullPageEditorPresetAPI = ExtractPresetAPI<ReturnType<typeof useFullPageEditorPreset>>;

export function useFullPageEditorPreset(props: FullPageEditorProps & WithFlagsProps) {
	const isLivePage = useIsLivePage();

	const { userId } = useSessionData();
	const [
		{
			deletedMentions,
			addedMentions,
			unrestrictedMentions,
			parentSpaceRestrictedMentions,
			pageRestrictedMentions,
			unrestrictedTasks,
			addedTasks,
			deletedTasks,
			pageRestrictedTasks,
			parentSpaceRestrictedTasks,
		},
		{
			setDeletedMentions,
			setAddedMentions,
			setAddedTasks,
			setDeletedTasks,
			setMentionsAdded,
			setTasksAdded,
		},
	] = useMentionsNotifications();
	const [mentionChanges, setMentionChanges] = useState<MentionType[]>([]);

	useEffect(() => {
		if (mentionChanges.length && isLivePage && fg('confluence_updated_mentions_livepages')) {
			handleMentionsChanged({
				mentionChanges,
				deletedMentions,
				addedMentions,
				setDeletedMentions,
				setAddedMentions,
				userId,
				unrestrictedMentions,
				parentSpaceRestrictedMentions,
				pageRestrictedMentions,
				unrestrictedTasks,
				addedTasks,
				deletedTasks,
				setAddedTasks,
				setDeletedTasks,
				pageRestrictedTasks,
				parentSpaceRestrictedTasks,
				setMentionsAdded,
				setTasksAdded,
			});
			setMentionChanges([]);
		}
		// We only need to trigger the handler function when mentionChanges gets updated
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mentionChanges]);

	const mentionsPluginInitialConfiguration = {
		handleMentionsChanged: (mentionChanges: MentionType[]) => {
			if (mentionChanges.length) {
				setMentionChanges(mentionChanges);
			}
		},
	};

	const tablesPluginInitialConfiguration = {
		tableResizingEnabled: true,
		isTableAlignmentEnabled: true,
	};

	const tasksAndDecisionsInitialConfiguration = fg('editor_request_to_edit_task')
		? {
				hasEditPermission: props.hasEditPermission,
				requestToEditContent: props.requestToEditContent,
				hasRequestedEditPermission: props.hasRequestedEditPermission,
			}
		: undefined;

	const initialPluginConfiguration = {
		mentionsPlugin: mentionsPluginInitialConfiguration,
		tablesPlugin: tablesPluginInitialConfiguration,
		tasksAndDecisionsPlugin: tasksAndDecisionsInitialConfiguration,
	};
	const universalPreset = useUniversalPreset({
		props,
		initialPluginConfiguration,
	});

	const isHeadingLinksEnabled = getIsHeadingLinksEnabled(true);
	const textHighlightingFloatingToolbarExperiment =
		props.aiGlobalOptIn.status === 'disabled' &&
		FeatureGates.getExperimentValue<boolean>(
			'editor_text_highlighting_floating_toolbar',
			'isEnabled',
			false,
		);

	const blockControlPluginEnabled = fg('platform_editor_element_drag_and_drop');
	const isEditorContextMenuEnabled = fg('platform_editor_context-menu-plugin');
	const isPageTitleEnterImprovementsEnabled = fg(
		'confluence_frontend_page_title_enter_improvements',
	);
	const isExtensionManagerPluginEnabled = fg('cc_page_experiences_anchor_macro_refresh');

	const editorAPIRef = useRef<FullPageEditorPresetAPI | null | undefined>(null);

	const { isPluginEnabled: isLoomPluginEnabled, pluginOptions: loomPluginOptions } =
		useLoomPluginOptions({
			EditorButtonComponent: EditorLoomButton,
			editorAPIRef,
		});

	const editorSSREnabled = useBooleanFeatureFlag('confluence.frontend.editor.ssr');

	// Memoise the preset otherwise we will re-render the editor too often
	const { preset, editorApi } = usePreset(() => {
		const { editorPluginAIProvider, aiGlobalOptIn } = props;
		return universalPreset
			.maybeAdd(
				[
					referentialityPlugin,
					{
						...props.referentialityContext.options,
						api: props.referentialityContext.api ?? undefined,
					},
				],
				Boolean(props.referentialityContext.plugin),
			)
			.maybeAdd(
				[
					PageNextEditorPlugin,
					{
						editorPluginAIProvider,
						aiGlobalOptIn,
					},
				],
				Boolean(aiGlobalOptIn.status !== 'disabled'),
			)
			.maybeAdd([loomPlugin, loomPluginOptions], isLoomPluginEnabled)
			.add([editorViewModePlugin, { mode: props.viewMode }])
			.add(editorViewModeEffectsPlugin)
			.add(selectionMarkerPlugin)
			.maybeAdd(ssrPlugin, editorSSREnabled)
			.maybeAdd(blockControlsPlugin, blockControlPluginEnabled)
			.maybeAdd(EditorHeadingLinksPlugin, isHeadingLinksEnabled)
			.add([highlightPlugin, { textHighlightingFloatingToolbarExperiment }])
			.maybeAdd(contextMenuPlugin, isEditorContextMenuEnabled)
			.maybeAdd(pageTitleUtilsPlugin, isPageTitleEnterImprovementsEnabled)
			.maybeAdd(fabricExtensionManagerPlugin, isExtensionManagerPluginEnabled);
		// The only thing that cause a re-creation of the preset is something in the
		// universal preset to be consistent with current behaviour (ie. this could
		// be a page width change via the `appearance` prop).
	}, [
		universalPreset,
		props.aiGlobalOptIn.status,
		isLoomPluginEnabled,
		loomPluginOptions,
		blockControlPluginEnabled,
		isHeadingLinksEnabled,
		isPageTitleEnterImprovementsEnabled,
	]);

	const { setEditorAPI } = props;

	useEffect(() => {
		if (!setEditorAPI) {
			return;
		}
		editorAPIRef.current = editorApi;
		setEditorAPI(editorApi || null);
	}, [editorApi, setEditorAPI]);

	useEffect(() => {
		if (props.viewMode === undefined) {
			return;
		}
		editorApi?.core?.actions?.execute(
			editorApi?.editorViewMode?.commands?.updateViewMode(props.viewMode),
		);
	}, [props.viewMode, editorApi]);

	useEffect(() => {
		if (!fg('editor_request_to_edit_task')) {
			return;
		}
		editorApi?.core?.actions?.execute(
			editorApi?.taskDecision?.commands?.updateEditPermission(props.hasEditPermission),
		);
	}, [props.hasEditPermission, editorApi]);

	useEffect(() => {
		if (!fg('editor_request_to_edit_task')) {
			return;
		}
		editorApi?.core?.actions?.execute(
			editorApi?.taskDecision?.commands?.updateHasRequestedEditPermission(
				props.hasRequestedEditPermission || false,
			),
		);
	}, [props.hasRequestedEditPermission, editorApi]);

	const { openLoomRecorder } = useLoomRecorderEntryPoint({
		flags: props.flags,
		entryPointLocation: 'editor-toolbar',
		resetInsertFunctionOnUnmount: true,
	});

	const loomProviderConfig: LoomProviderConfig = useMemo(
		() => ({ openLoomRecorder }),
		[openLoomRecorder],
	);

	useInitializeLoomPlugin({
		loomPlugin: editorApi?.loom,
		providerConfig: loomProviderConfig,
	});

	return preset;
}
